import React, { useMemo, useState } from 'react';
import { Box, Card, CardContent, Chip, Divider, ToggleButton, ToggleButtonGroup, Typography, alpha, useMediaQuery, useTheme } from '@mui/material';
import AccountBalanceWalletIcon from '@mui/icons-material/AccountBalanceWallet';
import ShowChartIcon from '@mui/icons-material/ShowChart';
import StackedBarChartIcon from '@mui/icons-material/StackedBarChart';
import {
  ComposedChart,
  Bar,
  Cell,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  ReferenceLine
} from 'recharts';
import { niceScale, formatAxisCurrency } from '../../utils/chartAxisUtils';
import { isProjectedMonth, projectedMonthLabel } from '../../utils/projectedMonth';

/**
 * Net worth chart shown above the Net Worth table. Plots the same month-end
 * series as the table's Net Worth row so the chart and table always agree.
 * Two modes: stacked bars per account, or a single total line. Actual
 * months draw solid; forecast months draw dashed (total mode) or with a
 * diagonal-hatch fill (stacked mode), matching the table's shaded
 * forecast columns.
 *
 * data: [{ month: 'Jan-25', netWorth: number, isForecast: boolean,
 *          accounts: { [accountId]: balance } }]
 */
const NetWorthSection = ({ data = [], accounts = [], formatCurrency, height = 300 }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  // Twelve month totals sit shoulder to shoulder on a phone-width chart, so
  // the labels (and the axis ticks they compete with) shrink below sm.
  const labelFontSize = isMobile ? 9 : 11;
  const tickFontSize = isMobile ? 10 : 12;
  const totalColor = theme.palette.primary.main;
  const surfaceColor = theme.palette.background.paper;
  const [mode, setMode] = useState('stacked');
  // Account highlighted via legend click; null = no highlight (all bars full).
  const [highlightId, setHighlightId] = useState(null);

  const activeAccounts = useMemo(() => accounts.filter(a => a.isActive), [accounts]);

  // Fixed hue assignment: account's own color, else a stable fallback keyed
  // to its position in the accounts list (never re-assigned as data changes).
  const colorForAccount = (account, index) =>
    account.color || `hsl(${(index * 137.5) % 360}, 70%, 50%)`;

  // Split the total series into solid (actual) and dashed (forecast)
  // segments; the forecast segment repeats the last actual point so the
  // dashed line connects to the solid one instead of starting with a gap.
  // stackTop is the top of the positive stack — where the month's total
  // data label is anchored so it sits above the bar, not at the net value.
  const chartData = useMemo(() => {
    const lastActualIdx = data.reduce((last, d, i) => (d.isForecast ? last : i), -1);
    return data.map((d, i) => {
      let stackTop = 0;
      activeAccounts.forEach(a => {
        const v = d.accounts?.[a.id];
        if (typeof v === 'number' && v > 0) stackTop += v;
      });
      return {
        ...d,
        // The projected current month repeats its own month name on the axis;
        // `month` stays the unique suffixed key so React keys don't collide.
        monthLabel: isProjectedMonth(d.month) ? projectedMonthLabel(d.month) : d.month,
        stackTop,
        actual: d.isForecast ? null : d.netWorth,
        forecast: d.isForecast || i === lastActualIdx ? d.netWorth : null
      };
    });
  }, [data, activeAccounts]);

  const forecastMonths = useMemo(() => data.filter(d => d.isForecast).map(d => d.month), [data]);
  const hasForecast = forecastMonths.length > 0;

  const yAxis = useMemo(() => {
    if (!data.length) return { domain: ['auto', 'auto'], ticks: undefined };
    if (mode === 'stacked') {
      // Sign-aware stack extents: positive balances stack up, negative
      // (loans, credit cards) stack down, so span both.
      let min = 0;
      let max = 0;
      data.forEach(d => {
        let pos = 0;
        let neg = 0;
        activeAccounts.forEach(a => {
          const v = d.accounts?.[a.id];
          if (typeof v === 'number') {
            if (v >= 0) pos += v; else neg += v;
          }
        });
        if (pos > max) max = pos;
        if (neg < min) min = neg;
      });
      if (min === 0 && max === 0) return { domain: ['auto', 'auto'], ticks: undefined };
      return niceScale(min, max);
    }
    // Total mode: net worth is a position (line), not a length (bar), so the
    // axis spans the data rather than being clamped to zero — otherwise a
    // large balance flattens the line into a straight rule.
    const values = data.map(d => d.netWorth).filter(v => typeof v === 'number' && isFinite(v));
    if (!values.length) return { domain: ['auto', 'auto'], ticks: undefined };
    return niceScale(Math.min(...values), Math.max(...values));
  }, [data, mode, activeAccounts]);

  const accountName = useMemo(() => {
    const map = new Map();
    accounts.forEach(a => map.set(a.id, a.name || a.id));
    return map;
  }, [accounts]);

  const CustomTooltip = ({ active, payload, label }) => {
    if (!active || !payload || !payload.length) return null;
    const point = payload[0]?.payload;
    if (!point) return null;
    const heading = `${label}${point.isForecast ? ' · Forecast' : ''}`;
    const rows = mode === 'stacked'
      ? activeAccounts
          .map((a, i) => ({ name: accountName.get(a.id), value: point.accounts?.[a.id] ?? 0, color: colorForAccount(a, i) }))
          .sort((a, b) => Math.abs(b.value) - Math.abs(a.value))
      : [];
    return (
      <Box sx={{ bgcolor: 'white', p: 1.5, border: 'none', borderRadius: 2, boxShadow: '0 4px 16px rgba(0,0,0,0.14)', minWidth: 168 }}>
        <Typography variant="caption" sx={{ display: 'block', mb: 0.75, fontWeight: 700, color: 'text.secondary' }}>
          {heading}
        </Typography>
        {rows.map((row, index) => (
          <Box key={`row-${index}`} sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 2, mb: 0.5 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', minWidth: 0 }}>
              <Box component="span" sx={{ width: 10, height: 10, borderRadius: '50%', bgcolor: row.color, flexShrink: 0, mr: 1 }} />
              <Typography variant="caption" noWrap sx={{ color: 'text.primary' }}>{row.name}</Typography>
            </Box>
            <Typography variant="caption" sx={{ fontWeight: 600, whiteSpace: 'nowrap', color: row.value < 0 ? 'error.main' : 'text.primary' }}>
              {formatCurrency(row.value)}
            </Typography>
          </Box>
        ))}
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 2, mt: rows.length ? 0.75 : 0, pt: rows.length ? 0.75 : 0, borderTop: rows.length ? '1px solid' : 'none', borderColor: 'divider' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', minWidth: 0 }}>
            {!rows.length && (
              <Box component="span" sx={{ width: 10, height: 10, borderRadius: '50%', bgcolor: totalColor, flexShrink: 0, mr: 1 }} />
            )}
            <Typography variant="caption" sx={{ fontWeight: 600, color: rows.length ? 'text.secondary' : 'text.primary' }}>Net Worth</Typography>
          </Box>
          <Typography variant="caption" sx={{ fontWeight: 700, whiteSpace: 'nowrap', color: point.netWorth < 0 ? 'error.main' : 'text.primary' }}>
            {formatCurrency(point.netWorth)}
          </Typography>
        </Box>
      </Box>
    );
  };

  // Which months get a data label. A phone-width chart gives each of twelve
  // bars ~25px, so labelling every one collides no matter how small the type;
  // on mobile we keep only the points that carry the story — first, last,
  // lowest and highest — matching the trend chart's mobile behaviour.
  const labelIndices = useMemo(() => {
    if (!isMobile || chartData.length < 2) return null;
    let minIdx = 0;
    let maxIdx = 0;
    chartData.forEach((d, i) => {
      if (d.netWorth < chartData[minIdx].netWorth) minIdx = i;
      if (d.netWorth > chartData[maxIdx].netWorth) maxIdx = i;
    });
    return new Set([0, chartData.length - 1, minIdx, maxIdx]);
  }, [chartData, isMobile]);

  // Per-month net worth total, drawn above the positive stack (compact
  // axis-style formatting so twelve labels fit without colliding).
  const renderTotalLabel = ({ x, y, index }) => {
    const point = chartData[index];
    if (!point || typeof x !== 'number' || typeof y !== 'number') return null;
    if (labelIndices && !labelIndices.has(index)) return null;
    return (
      <text
        x={x}
        y={y - (isMobile ? 6 : 8)}
        textAnchor="middle"
        fontSize={labelFontSize}
        fontWeight={600}
        fill={theme.palette.text.secondary}
      >
        {formatAxisCurrency(point.netWorth)}
      </text>
    );
  };

  if (!data.length) return null;

  return (
    <Card sx={{ mb: 3 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', px: 2, py: 1.5, gap: 1.5 }}>
        <Typography variant="h6" sx={{ fontWeight: 'bold', flex: 1, display: 'flex', alignItems: 'center', gap: 1 }}>
          <Box component="span" sx={{ display: 'inline-flex', color: 'text.secondary', '& svg': { fontSize: '1.1rem' } }}>
            <AccountBalanceWalletIcon />
          </Box>
          Net Worth
        </Typography>
        {hasForecast && (
          <Typography variant="caption" sx={{ color: 'text.secondary', display: 'flex', alignItems: 'center', gap: 0.75 }}>
            {mode === 'stacked' ? (
              <Box
                component="span"
                sx={{
                  width: 14,
                  height: 14,
                  borderRadius: 0.5,
                  backgroundImage: `repeating-linear-gradient(135deg, ${alpha(theme.palette.text.primary, 0.45)} 0 2px, ${alpha(theme.palette.text.primary, 0.1)} 2px 5px)`
                }}
              />
            ) : (
              <Box component="span" sx={{ width: 18, borderTop: `2px dashed ${totalColor}` }} />
            )}
            Forecast
          </Typography>
        )}
        <ToggleButtonGroup
          size="small"
          value={mode}
          exclusive
          onChange={(_, val) => { if (val) setMode(val); }}
          aria-label="Net worth chart mode"
          sx={{ '& .MuiToggleButton-root': { px: 0.5, py: 0.25, minHeight: 28 } }}
        >
          <ToggleButton value="stacked" aria-label="Stacked bars by account">
            <StackedBarChartIcon sx={{ fontSize: 16 }} />
          </ToggleButton>
          <ToggleButton value="total" aria-label="Total only">
            <ShowChartIcon sx={{ fontSize: 16 }} />
          </ToggleButton>
        </ToggleButtonGroup>
      </Box>
      <Divider />
      <CardContent>
        <Box sx={{ height, bgcolor: 'grey.50', borderRadius: 1 }}>
          <ResponsiveContainer width="100%" height="100%">
            <ComposedChart data={chartData} margin={{ top: mode === 'stacked' ? 24 : 10, right: 30, left: 0, bottom: 0 }} stackOffset="sign">
              <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#eceff1" />
              <XAxis dataKey="monthLabel" tick={{ fontSize: tickFontSize, fill: '#78909c' }} tickMargin={8} axisLine={{ stroke: '#e0e0e0' }} tickLine={false} />
              <YAxis tick={{ fontSize: tickFontSize, fill: '#78909c' }} tickMargin={6} tickFormatter={formatAxisCurrency} domain={yAxis.domain} ticks={yAxis.ticks} axisLine={false} tickLine={false} />
              <Tooltip
                content={<CustomTooltip />}
                cursor={mode === 'stacked'
                  ? { fill: alpha(theme.palette.text.primary, 0.05) }
                  : { stroke: '#b0bec5', strokeWidth: 1 }}
                wrapperStyle={{ fontSize: 11 }}
              />
              {mode === 'stacked' && (
                <defs>
                  {activeAccounts.map((account, index) => {
                    const color = colorForAccount(account, index);
                    return (
                      <pattern
                        key={account.id}
                        id={`nw-hatch-${account.id}`}
                        patternUnits="userSpaceOnUse"
                        width="6"
                        height="6"
                        patternTransform="rotate(45)"
                      >
                        <rect width="6" height="6" fill={color} fillOpacity="0.3" />
                        <rect width="3" height="6" fill={color} />
                      </pattern>
                    );
                  })}
                </defs>
              )}
              {Array.isArray(yAxis.domain) && yAxis.domain[0] < 0 && (
                <ReferenceLine y={0} stroke="#b0bec5" strokeWidth={1} />
              )}
              {mode === 'stacked' && activeAccounts.map((account, index) => (
                <Bar
                  key={account.id}
                  dataKey={`accounts.${account.id}`}
                  name={accountName.get(account.id)}
                  stackId="networth"
                  stroke={surfaceColor}
                  strokeWidth={1}
                  fill={colorForAccount(account, index)}
                  maxBarSize={40}
                  isAnimationActive={true}
                >
                  {chartData.map(d => (
                    <Cell
                      key={d.month}
                      fill={d.isForecast ? `url(#nw-hatch-${account.id})` : colorForAccount(account, index)}
                      fillOpacity={highlightId ? (account.id === highlightId ? 1 : 0.15) : 0.85}
                    />
                  ))}
                </Bar>
              ))}
              {mode === 'stacked' && (
                <Line
                  dataKey="stackTop"
                  stroke="none"
                  dot={false}
                  activeDot={false}
                  legendType="none"
                  isAnimationActive={false}
                  label={renderTotalLabel}
                />
              )}
              {mode === 'total' && (
                <Line
                  type="monotone"
                  dataKey="actual"
                  name="Net Worth"
                  stroke={totalColor}
                  strokeWidth={2}
                  dot={{ r: 3, stroke: totalColor, strokeWidth: 1, fill: 'white' }}
                  activeDot={{ r: 5, stroke: totalColor, strokeWidth: 2, fill: 'white' }}
                  connectNulls={false}
                />
              )}
              {mode === 'total' && hasForecast && (
                <Line
                  type="monotone"
                  dataKey="forecast"
                  name="Net Worth (forecast)"
                  stroke={totalColor}
                  strokeWidth={2}
                  strokeDasharray="5 5"
                  dot={{ r: 3, stroke: totalColor, strokeWidth: 1, fill: 'white' }}
                  activeDot={{ r: 5, stroke: totalColor, strokeWidth: 2, fill: 'white' }}
                  connectNulls={false}
                />
              )}
            </ComposedChart>
          </ResponsiveContainer>
        </Box>
        {mode === 'stacked' && (
          <Box sx={{ display: 'flex', flexWrap: 'wrap', justifyContent: 'center', gap: 0.75, mt: 2 }}>
            {activeAccounts.map((account, index) => {
              const color = colorForAccount(account, index);
              const selected = highlightId === account.id;
              return (
                <Chip
                  key={account.id}
                  size="small"
                  onClick={() => setHighlightId(prev => (prev === account.id ? null : account.id))}
                  aria-pressed={selected}
                  icon={<Box sx={{ width: 8, height: 8, ml: '6px !important', borderRadius: '50%', bgcolor: color }} />}
                  label={accountName.get(account.id)}
                  sx={{
                    height: 24,
                    fontSize: '0.75rem',
                    color: 'text.primary',
                    bgcolor: alpha(color, selected ? 0.3 : 0.12),
                    '&:hover': { bgcolor: alpha(color, selected ? 0.35 : 0.2) },
                    border: '1px solid',
                    borderColor: selected ? color : 'transparent',
                    opacity: highlightId && !selected ? 0.5 : 1,
                    fontWeight: selected ? 600 : 400
                  }}
                />
              );
            })}
          </Box>
        )}
      </CardContent>
    </Card>
  );
};

export default NetWorthSection;
